/**
 * Deep cloning utility for performance optimization
 * Replaces expensive JSON.parse(JSON.stringify()) pattern
 */

/**
 * Deep clones an object using structuredClone API (modern browsers)
 * Falls back to JSON method if structuredClone is not available
 * 
 * @param obj - Object to clone
 * @returns Deep cloned copy of the object
 */
export function deepClone<T>(obj: T): T {
    if (obj === null || obj === undefined) {
        return obj;
    }

    // Use structuredClone if available (modern browsers, faster than JSON method)
    if (typeof structuredClone === 'function') {
        try {
            return structuredClone(obj);
        } catch (error) {
            // Fall back to JSON method if structuredClone fails
            console.warn('structuredClone failed, falling back to JSON method:', error);
        }
    }

    // Fallback to JSON method (supports older browsers)
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (error) {
        console.error('Deep clone failed:', error);
        return obj;
    }
}

/**
 * Deep clones an array using structuredClone API
 * 
 * @param arr - Array to clone
 * @returns Deep cloned copy of the array
 */
export function deepCloneArray<T>(arr: T[]): T[] {
    if (!Array.isArray(arr)) {
        return arr;
    }
    return deepClone(arr);
}
